
import React, { useMemo } from 'react';
import {
  TrendingUp, Users, Wallet, Clock,
  Activity, Target, CheckCircle2, BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency, formatKwh, formatPercent, PARCEIRO_FAIXAS } from '../../../../src/lib/comissaoUtils';
import { REGRA_BONIFICACAO, limitesFaixa } from '../../../../src/lib/bonificacaoParceiro';
import PremiacaoTrimestralCard from '../components/PremiacaoTrimestralCard';
import { MOSTRAR_ITENS_V2_EM_DISCUSSAO } from '../../../../src/lib/politicaComercial';
import type { ComissaoVendedor, ComissaoParceiro } from '../../../../types';

interface Props {
  comissoesVendedores: ComissaoVendedor[];
  comissoesParceiros: ComissaoParceiro[];
  mes: number;
  ano: number;
}

const MESES_FULL = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const TT_STYLE: React.CSSProperties = {
  backgroundColor: '#1d220f',
  borderRadius: '10px',
  border: '1px solid rgba(95,108,55,0.4)',
  color: '#fff',
  padding: '8px 12px',
};

const DashboardTab: React.FC<Props> = ({ comissoesVendedores, comissoesParceiros, mes, ano }) => {
  const normais = useMemo(() => comissoesParceiros.filter(c => !c.is_grupo_a), [comissoesParceiros]);

  const stats = useMemo(() => {
    const totalVend = comissoesVendedores.reduce((s, c) => s + c.valor_total, 0);
    const totalParc = normais.reduce((s, c) => s + c.valor_base_comissao, 0);
    const totalFolha = totalVend + totalParc;
    // Regra nova não tem avaliação manual de Grupo A (não pode assinar)
    const grupoAPend = comissoesParceiros.filter(c => c.is_grupo_a && !c.regra && c.grupo_a_status === 'pendente_aprovacao_gerente').length;
    const parcelasPend = normais.filter(c => c.parcela_1_status === 'pendente' || c.parcela_2_status === 'pendente').length;
    const vendPagos = comissoesVendedores.filter(c => c.status === 'pago').length;
    const vendTotal = comissoesVendedores.length;
    return { totalFolha, totalVend, totalParc, grupoAPend, parcelasPend, vendPagos, vendTotal };
  }, [comissoesVendedores, normais, comissoesParceiros]);

  const parceirosAgrupados = useMemo(() => {
    const map = new Map<string, { nome: string; negs: number; kwhTotal: number; valorTotal: number; p1Pago: number; p2Pago: number }>();
    normais.forEach(c => {
      const pid = c.parceiro_id;
      if (!map.has(pid)) map.set(pid, { nome: c.parceiro?.full_name || 'N/A', negs: 0, kwhTotal: 0, valorTotal: 0, p1Pago: 0, p2Pago: 0 });
      const e = map.get(pid)!;
      e.negs++;
      e.kwhTotal += c.consumo_medio_kwh;
      e.valorTotal += c.valor_base_comissao;
      if (c.parcela_1_status === 'pago') e.p1Pago++;
      if (c.parcela_2_status === 'pago') e.p2Pago++;
    });
    return [...map.values()].sort((a, b) => b.valorTotal - a.valorTotal);
  }, [normais]);

  const sellerChartData = useMemo(() =>
    [...comissoesVendedores]
      .sort((a, b) => b.valor_total - a.valor_total)
      .map(c => ({
        nome: c.vendedor?.full_name?.split(' ')[0] || 'N/A',
        salario: c.salario_base,
        comissao: c.valor_comissao_kwh,
        bonus: c.valor_bonus,
      })),
    [comissoesVendedores],
  );

  // Tier breakdown — clear named ranges instead of confusing percentages
  const tierData = useMemo(() => {
    const result: { faixa: string; qtd: number; valorTotal: number; pct: number }[] = [];
    const total = normais.reduce((s, c) => s + c.valor_base_comissao, 0);

    // Regra nova (aceite a partir de 26/10/2026): faixa do mês do parceiro (F1..F6).
    // Não dá para agrupar pelo percentual — 60% e 80% existem nas duas tabelas.
    REGRA_BONIFICACAO.faixas.forEach((f, i) => {
      const nome = `F${i + 1}`;
      const matching = normais.filter(c => c.regra && c.faixa_mes === nome);
      if (matching.length === 0) return;
      const val = matching.reduce((s, c) => s + c.valor_base_comissao, 0);
      const { de, ate } = limitesFaixa(i);
      const kwh = ate === Infinity ? `acima de ${(de - 1).toLocaleString('pt-BR')} kWh` : `${de.toLocaleString('pt-BR')} – ${ate.toLocaleString('pt-BR')} kWh`;
      result.push({
        faixa: `${nome} · ${Math.round(f[1] * 100)}% · ${kwh} no mês`,
        qtd: matching.length,
        valorTotal: val,
        pct: total > 0 ? (val / total) * 100 : 0,
      });
    });

    // Regra antiga: faixa pelo consumo do próprio consumidor
    PARCEIRO_FAIXAS.forEach(f => {
      const matching = normais.filter(c => !c.regra && c.faixa_comissao_percentual === f.percentual);
      if (matching.length === 0) return;
      const val = matching.reduce((s, c) => s + c.valor_base_comissao, 0);
      const label = f.max === Infinity
        ? `Acima de ${f.min.toLocaleString('pt-BR')} kWh`
        : `${f.min.toLocaleString('pt-BR')} – ${f.max.toLocaleString('pt-BR')} kWh`;
      result.push({
        faixa: label,
        qtd: matching.length,
        valorTotal: val,
        pct: total > 0 ? (val / total) * 100 : 0,
      });
    });
    return result.sort((a, b) => b.valorTotal - a.valorTotal);
  }, [normais]);

  return (
    <div className="space-y-6">

      {/* ── KPI Row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" style={{ animation: 'fadeSlideUp 0.5s ease-out' }}>
        {/* Grand Total */}
        <div className="col-span-2 relative overflow-hidden rounded-2xl bg-[#212913] p-6 shadow-xl">
          <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-white/5 blur-sm pointer-events-none" />
          <div className="absolute right-4 bottom-4 w-16 h-16 rounded-full bg-[#F6A937]/10 pointer-events-none" />
          <p className="text-[10px] font-bold text-white/50 uppercase tracking-[0.18em] mb-2">
            {MESES_FULL[mes - 1]} {ano} — Folha Total
          </p>
          <p className="text-4xl font-black text-white tabular-nums leading-none">
            {formatCurrency(stats.totalFolha)}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
            <div>
              <p className="text-[9px] text-white/40 uppercase font-semibold mb-0.5">Vendedores</p>
              <p className="text-sm font-bold text-white/80 tabular-nums">{formatCurrency(stats.totalVend)}</p>
            </div>
            <div className="w-px h-7 bg-white/10 hidden sm:block" />
            <div>
              <p className="text-[9px] text-white/40 uppercase font-semibold mb-0.5">Parceiros</p>
              <p className="text-sm font-bold text-[#F6A937] tabular-nums">{formatCurrency(stats.totalParc)}</p>
            </div>
            <div className="w-px h-7 bg-white/10 hidden sm:block" />
            <div>
              <p className="text-[9px] text-white/40 uppercase font-semibold mb-0.5">Equipe</p>
              <p className="text-sm font-bold text-white/70">{stats.vendTotal} vend. · {parceirosAgrupados.length} parc.</p>
            </div>
          </div>
        </div>

        {/* Vendedores Pagos */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm" style={{ animation: 'fadeSlideUp 0.6s ease-out' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 rounded-xl bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 flex items-center justify-center">
              <Users size={16} className="text-[#5F6C37]" />
            </div>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
              stats.vendPagos === stats.vendTotal && stats.vendTotal > 0
                ? 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]'
                : 'bg-[#F6A937]/15 text-[#b85c00] dark:text-[#F6A937]'
            }`}>
              {stats.vendPagos}/{stats.vendTotal} pagos
            </span>
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wide">Vendedores</p>
          <p className="text-xl font-black text-gray-900 dark:text-white tabular-nums mt-1">{formatCurrency(stats.totalVend)}</p>
          <div className="mt-3 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[rgba(95,108,55,0.9)] to-[#84a52d] transition-all duration-700"
              style={{ width: stats.vendTotal > 0 ? `${(stats.vendPagos / stats.vendTotal) * 100}%` : '0%' }}
            />
          </div>
        </div>

        {/* Pendências */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm" style={{ animation: 'fadeSlideUp 0.7s ease-out' }}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              stats.parcelasPend > 0 ? 'bg-[#F6A937]/15' : 'bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20'
            }`}>
              <Clock size={16} className={stats.parcelasPend > 0 ? 'text-[#F6A937]' : 'text-[#5F6C37]'} />
            </div>
            {stats.grupoAPend > 0 && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                {stats.grupoAPend} Grupo A
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wide">Parcelas Pendentes</p>
          <p className="text-2xl font-black text-gray-900 dark:text-white mt-1">{stats.parcelasPend}</p>
          {stats.parcelasPend === 0 && (
            <div className="mt-1 flex items-center gap-1 text-[#5F6C37]">
              <CheckCircle2 size={12} />
              <span className="text-[10px] font-semibold">Todas em dia</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stacked bar: Sellers */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm" style={{ animation: 'fadeSlideUp 0.65s ease-out' }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-sm text-gray-800 dark:text-white flex items-center gap-2">
              <TrendingUp size={15} className="text-[#5F6C37]" />
              Composição Salarial — Vendedores
            </h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">{MESES_FULL[mes - 1]} {ano}</span>
          </div>
          {sellerChartData.length > 0 ? (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sellerChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" vertical={false} strokeOpacity={0.08} />
                  <XAxis dataKey="nome" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={TT_STYLE} labelStyle={{ color: '#fff', fontWeight: 700 }} itemStyle={{ color: '#d1fae5' }} formatter={(v: number) => formatCurrency(v)} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Bar dataKey="salario" name="Salário Base" fill="#84a52d" barSize={22} stackId="s" />
                  <Bar dataKey="comissao" name="Comissão kWh" fill="#5F6C37" barSize={22} stackId="s" />
                  <Bar dataKey="bonus" name="Bônus" fill="#F6A937" radius={[4, 4, 0, 0]} barSize={22} stackId="s" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[260px] flex flex-col items-center justify-center gap-3 text-gray-300 dark:text-gray-600">
              <Activity size={36} />
              <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma comissão calculada neste período</p>
            </div>
          )}
        </div>

        {/* Tier Breakdown — clear horizontal bars with named ranges */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm" style={{ animation: 'fadeSlideUp 0.75s ease-out' }}>
          <h3 className="font-bold text-sm text-gray-800 dark:text-white flex items-center gap-2 mb-5">
            <BarChart3 size={15} className="text-[#F6A937]" />
            Distribuição por Faixa — Parceiros
          </h3>
          {tierData.length > 0 ? (
            <div className="space-y-4">
              {tierData.map((t, i) => {
                const colors = ['#5F6C37', '#F6A937', '#84a52d', '#d97706', '#4d5a2c', '#b45309'];
                const color = colors[i % colors.length];
                return (
                  <div key={t.faixa} style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{t.faixa}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                        {t.qtd} neg.
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max(t.pct, 3)}%`, backgroundColor: color }}
                      />
                    </div>
                    <p className="text-[11px] font-bold tabular-nums mt-1" style={{ color }}>
                      {formatCurrency(t.valorTotal)}
                      <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">({t.pct.toFixed(0)}%)</span>
                    </p>
                  </div>
                );
              })}
              {tierData.length === 0 && (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">Sem dados</p>
              )}
            </div>
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">Sem dados de parceiros</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Sellers Performance Cards — ABOVE partners ── */}
      {comissoesVendedores.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden" style={{ animation: 'fadeSlideUp 0.8s ease-out' }}>
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <Target size={14} className="text-[#5F6C37]" />
            <h3 className="font-bold text-sm text-gray-800 dark:text-white">Atingimento de Metas — Vendedores</h3>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...comissoesVendedores]
              .sort((a, b) => b.percentual_atingimento - a.percentual_atingimento)
              .map((c, idx) => {
                const initials = (c.vendedor?.full_name || 'NA').split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                const bateuMeta = c.percentual_atingimento >= 100;
                // Fix: bar fills 100% when goal is met, and proportionally otherwise
                const barW = bateuMeta ? 100 : Math.min(c.percentual_atingimento, 100);
                const statusCls = c.status === 'pago'
                  ? 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]'
                  : c.status === 'aprovado'
                    ? 'bg-[#F6A937]/15 text-[#b85c00] dark:text-[#F6A937]'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
                const statusLabel = c.status === 'pago' ? 'Pago' : c.status === 'aprovado' ? 'Aprovado' : 'Calculado';
                return (
                  <div key={c.id} className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 border border-gray-100 dark:border-gray-600/50 hover:shadow-md transition-shadow" style={{ animation: `fadeSlideUp ${0.5 + idx * 0.06}s ease-out` }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 dark:text-white truncate leading-tight">
                          {c.vendedor?.full_name?.split(' ').slice(0, 2).join(' ') || 'N/A'}
                        </p>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${statusCls}`}>{statusLabel}</span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between text-[11px] mb-1.5">
                        <span className="text-gray-400 dark:text-gray-500">{c.meta_kwh > 0 ? `${c.meta_kwh.toLocaleString('pt-BR')} kWh` : 'Sem meta'}</span>
                        <span className={`font-black ${bateuMeta ? 'text-[rgba(95,108,55,0.9)] dark:text-[#84cc16]' : 'text-[#F6A937]'}`}>
                          {formatPercent(c.percentual_atingimento)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${bateuMeta ? 'bg-gradient-to-r from-[rgba(95,108,55,0.9)] to-[#84a52d]' : 'bg-[#F6A937]'}`}
                          style={{ width: `${barW}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{formatKwh(c.total_kwh_vendido)}</span>
                      <span className="font-black text-sm text-gray-900 dark:text-white tabular-nums">{formatCurrency(c.valor_total)}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Premiação Trimestral (Nova Política Comercial) ── */}
      {MOSTRAR_ITENS_V2_EM_DISCUSSAO && <PremiacaoTrimestralCard mes={mes} ano={ano} />}

      {/* ── Partners Summary — BELOW sellers ── */}
      {parceirosAgrupados.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden" style={{ animation: 'fadeSlideUp 0.9s ease-out' }}>
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <Wallet size={14} className="text-[#F6A937]" />
            <h3 className="font-bold text-sm text-gray-800 dark:text-white">Resumo por Parceiro</h3>
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{parceirosAgrupados.length} ativos neste mês</span>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
            {parceirosAgrupados.map((p, idx) => {
              const initials = p.nome.split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
              const p1All = p.negs;
              const p1Ok = p.p1Pago === p1All && p1All > 0;
              const p2Ok = p.p2Pago === p1All && p1All > 0;
              return (
                <div key={idx} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/70 dark:hover:bg-gray-700/20 transition-colors">
                  <span className="w-5 text-xs font-bold text-gray-300 dark:text-gray-600 tabular-nums">{idx + 1}</span>
                  <div className="w-10 h-10 rounded-xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow-sm">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{p.nome}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{p.negs} neg. · {formatKwh(p.kwhTotal)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-black text-gray-900 dark:text-white tabular-nums text-sm">{formatCurrency(p.valorTotal)}</p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">total comissão</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase">P1</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${p1Ok ? 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]' : 'bg-[#F6A937]/15 text-[#b85c00] dark:text-[#F6A937]'}`}>
                        {p.p1Pago}/{p1All}
                      </span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase">P2</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${p2Ok ? 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {p.p2Pago}/{p1All}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Motion keyframes */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default DashboardTab;
